import * as React from "react"

import { Check, Copy } from "lucide-react"

import { useCopyToClipboardFeedback } from "../../hooks/useCopyToClipboardFeedback"
import { Button, type ButtonProps } from "../Button/Button"
import { CopyAnnouncement } from "./CopyAnnouncement"

/**
 * `tooltip` and `tooltipProps` are the component's own: the tooltip is how the
 * copy confirms itself, so a caller-supplied one would replace the feedback
 * rather than add to it. `label`, `copiedLabel`, and `tooltipSide` are what a
 * caller has instead.
 */
export interface CopyButtonProps extends Omit<
  ButtonProps,
  "value" | "children" | "onCopy" | "tooltip" | "tooltipProps"
> {
  /** The text written to the clipboard on click. */
  value: string
  /** Accessible label and default tooltip text. Defaults to "Copy". */
  label?: string
  /** Tooltip text shown after a successful copy. Defaults to "Copied!". */
  copiedLabel?: string
  /** Which side of the button the tooltip is placed on. */
  tooltipSide?: "top" | "right" | "bottom" | "left"
  /** Called with the copied value after a successful copy. */
  onCopy?: (value: string) => void
}

/**
 * Icon button that copies `value` to the clipboard and confirms the action by
 * swapping the copy icon for a check mark for a couple of seconds. Use anywhere
 * an ID, key, or other value can be copied so the copy always has visible
 * feedback.
 */
const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      value,
      label = "Copy",
      copiedLabel = "Copied!",
      tooltipSide = "top",
      variant = "secondary",
      onCopy,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    const { copied, copy } = useCopyToClipboardFeedback()

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event)
      void (async () => {
        if (await copy(value)) {
          onCopy?.(value)
        }
      })()
    }

    return (
      <>
        <Button
          ref={ref}
          type="button"
          icon
          tooltip={copied ? copiedLabel : label}
          tooltipProps={{ side: tooltipSide, forceOpen: copied }}
          variant={variant}
          onClick={handleClick}
          aria-label={label}
          className={className}
          {...props}
        >
          {copied ? (
            <Check className="size-3.5 text-coval-success-text" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </Button>
        <CopyAnnouncement message={copied ? copiedLabel : null} />
      </>
    )
  },
)

CopyButton.displayName = "CopyButton"

export { CopyButton }
